/**
 * Editor для ref.customer_cards (Порція Роздріб.5b).
 * bonus_balance — read-only (модифікується тільки через doc.loyalty_transactions).
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { contractors as contractorsApi, orgs } from '@/api/catalogs';
import {
  retailCustomerCards,
  type CustomerCardStatus,
} from '@/api/retailCustomerCards';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import RequirePermission from '@/auth/RequirePermission';
import { validateRequired } from '@/utils/validateRequired';
import { extractApiError } from '@/utils/extractApiError';
import RequiredLabel from '@/components/RequiredLabel';
import DatePopover from '@/components/DatePopover';

const PERMISSION = 'retail.customer_card.manage';

interface FormState {
  organization_id: string;
  card_code: string;
  customer_contractor_id: string;
  holder_name: string;
  holder_phone: string;
  holder_email: string;
  holder_birthdate: string;
  bonus_balance: string;
  status: CustomerCardStatus;
  expires_at: string;
  comment: string;
}

const EMPTY: FormState = {
  organization_id: '',
  card_code: '',
  customer_contractor_id: '',
  holder_name: '',
  holder_phone: '',
  holder_email: '',
  holder_birthdate: '',
  bonus_balance: '0',
  status: 'active',
  expires_at: '',
  comment: '',
};

export default function CustomerCardEditor() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const isNew = !id || id === 'new';

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const { availableOrgs } = usePermissionsByOrg(PERMISSION);
  const contQ = useQuery({
    queryKey: ['contractors-all'],
    queryFn: () => contractorsApi.list({ limit: 500 }),
  });

  const [form, setForm] = useState<FormState>(EMPTY);
  const [error, setError] = useState<string | null>(null);

  const docQuery = useQuery({
    queryKey: ['retail-customer-card', id],
    queryFn: () => retailCustomerCards.get(id!),
    enabled: !isNew,
  });

  useEffect(() => {
    if (isNew && availableOrgs.length > 0 && !form.organization_id) {
      setForm((f) => ({ ...f, organization_id: availableOrgs[0]!.id }));
    }
  }, [isNew, availableOrgs, form.organization_id]);

  useEffect(() => {
    if (!isNew && docQuery.data) {
      const d = docQuery.data;
      setForm({
        organization_id: d.organization_id,
        card_code: d.card_code,
        customer_contractor_id: d.customer_contractor_id ?? '',
        holder_name: d.holder_name ?? '',
        holder_phone: d.holder_phone ?? '',
        holder_email: d.holder_email ?? '',
        holder_birthdate: d.holder_birthdate ?? '',
        bonus_balance: d.bonus_balance,
        status: d.status,
        expires_at: d.expires_at?.slice(0, 16) ?? '',
        comment: d.comment ?? '',
      });
    }
  }, [isNew, docQuery.data]);

  const create = useMutation({
    mutationFn: () =>
      retailCustomerCards.create({
        organization_id: form.organization_id,
        card_code: form.card_code,
        customer_contractor_id: form.customer_contractor_id || null,
        holder_name: form.holder_name || null,
        holder_phone: form.holder_phone || null,
        holder_email: form.holder_email || null,
        holder_birthdate: form.holder_birthdate || null,
        bonus_balance: form.bonus_balance,
        status: form.status,
        expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
        comment: form.comment || null,
      }),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['retail-customer-cards'] });
      navigate(`/retail/customer-cards/${doc.id}`);
    },
    onError: (e) => setError(extractApiError(e)),
  });

  const update = useMutation({
    mutationFn: () =>
      retailCustomerCards.update(id!, {
        card_code: form.card_code,
        customer_contractor_id: form.customer_contractor_id || null,
        holder_name: form.holder_name || null,
        holder_phone: form.holder_phone || null,
        holder_email: form.holder_email || null,
        holder_birthdate: form.holder_birthdate || null,
        status: form.status,
        expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
        comment: form.comment || null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['retail-customer-cards'] });
      qc.invalidateQueries({ queryKey: ['retail-customer-card', id] });
    },
    onError: (e) => setError(extractApiError(e)),
  });

  const remove = useMutation({
    mutationFn: () => retailCustomerCards.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['retail-customer-cards'] });
      navigate('/retail/customer-cards');
    },
    onError: (e) => setError(extractApiError(e)),
  });

  const handleSave = () => {
    setError(null);
    const err = validateRequired(
      {
        organization_id: form.organization_id,
        card_code: form.card_code,
      },
      {
        organization_id: 'Юрособа',
        card_code: 'Код картки',
      },
    );
    if (err) { setError(err); return; }
    if (isNew) create.mutate();
    else update.mutate();
  };

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew ? 'Нова картка клієнта' : `Картка: ${form.card_code}`}
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button onClick={() => navigate('/retail/customer-cards')}>Скасувати</button>
          <RequirePermission code={PERMISSION}>
            <button
              className="primary"
              onClick={handleSave}
              disabled={create.isPending || update.isPending}
            >
              Зберегти
            </button>
          </RequirePermission>
          {!isNew && (
            <RequirePermission code={PERMISSION}>
              <button
                className="danger"
                onClick={() => {
                  if (window.confirm('Видалити картку?')) remove.mutate();
                }}
              >
                Видалити
              </button>
            </RequirePermission>
          )}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
        <div className="field">
          <RequiredLabel>Організація</RequiredLabel>
          {isNew ? (
            <select
              value={form.organization_id}
              onChange={(e) => setForm({ ...form, organization_id: e.target.value })}
            >
              <option value="">—</option>
              {availableOrgs.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.code} — {o.name}
                </option>
              ))}
            </select>
          ) : (
            <div>
              {orgList.data?.items.find((o) => o.id === form.organization_id)?.name ??
                form.organization_id}
            </div>
          )}
        </div>
        <div className="field">
          <RequiredLabel>Код картки</RequiredLabel>
          <input
            value={form.card_code}
            onChange={(e) => setForm({ ...form, card_code: e.target.value })}
            maxLength={50}
          />
        </div>
        <div className="field">
          <label>ПIБ власника</label>
          <input
            value={form.holder_name}
            onChange={(e) => setForm({ ...form, holder_name: e.target.value })}
            maxLength={255}
          />
        </div>
        <div className="field">
          <label>Телефон</label>
          <input
            value={form.holder_phone}
            onChange={(e) => setForm({ ...form, holder_phone: e.target.value })}
            maxLength={20}
          />
        </div>
        <div className="field">
          <label>Email</label>
          <input
            type="email"
            value={form.holder_email}
            onChange={(e) => setForm({ ...form, holder_email: e.target.value })}
            maxLength={255}
          />
        </div>
        <div className="field">
          <label>Дата народження</label>
          <DatePopover
            value={form.holder_birthdate}
            onChange={(v) => setForm({ ...form, holder_birthdate: v })}
          />
        </div>
        <div className="field">
          <label>Контрагент (опц.)</label>
          <select
            value={form.customer_contractor_id}
            onChange={(e) => setForm({ ...form, customer_contractor_id: e.target.value })}
          >
            <option value="">—</option>
            {contQ.data?.items.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>Бонусний баланс</label>
          <input value={form.bonus_balance} disabled />
          <small style={{ color: '#64748b' }}>
            Змінюється тільки через бонусні транзакції
          </small>
        </div>
        <div className="field">
          <label>Статус</label>
          <select
            value={form.status}
            onChange={(e) =>
              setForm({ ...form, status: e.target.value as CustomerCardStatus })
            }
          >
            <option value="active">Активна</option>
            <option value="blocked">Блокована</option>
            <option value="lost">Загублена</option>
            <option value="expired">Прострочена</option>
          </select>
        </div>
        <div className="field">
          <label>Діє до</label>
          <DatePopover
            withTime
            value={form.expires_at}
            onChange={(v) => setForm({ ...form, expires_at: v })}
          />
        </div>
        <div className="field" style={{ gridColumn: 'span 2' }}>
          <label>Коментар</label>
          <textarea
            value={form.comment}
            onChange={(e) => setForm({ ...form, comment: e.target.value })}
            rows={2}
          />
        </div>
      </div>

      {error && (
        <div className="error" style={{ marginTop: '1rem', color: '#b91c1c' }}>
          {error}
        </div>
      )}
    </div>
  );
}
